import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PageLoader, PageHeader } from "@/components/shared";
import { InvitePartner } from "@/components/InvitePartner";
import { JOURNEY_STAGES, stageIndex, currentStageKey, isAtOrPast } from "@/lib/journey";
import { useMilestones } from "@/components/CelebrationMoment";
import { ContractTimeline } from "@/components/ContractTimeline";
import { ReviewsReferrals } from "@/components/ReviewsReferrals";
import { ArrowRight, Sparkles, Check, KeyRound } from "lucide-react";

export default function BuyerDashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["workspace"],
    queryFn: async () => (await api.get("/buyer/workspace")).data,
  });
  const { data: ms } = useMilestones();
  if (isLoading) return <PageLoader />;

  const { roadmap, agent, client, household } = data;
  const curKey = currentStageKey(client);
  const curIdx = stageIndex(curKey);
  const curStage = JOURNEY_STAGES[curIdx];
  const pct = Math.round((curIdx / (JOURNEY_STAGES.length - 1)) * 100);

  return (
    <div>
      <PageHeader
        title={`Welcome, ${client.name?.split(" ")[0] || "there"} 👋`}
        subtitle="Here's where you are on your journey, and the one next step to focus on. No rush — one stage at a time."
      />

      <div className="grid lg:grid-cols-3 gap-6">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-2 hg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-semibold">Your journey</h2>
            <span className="text-sm font-semibold text-primary">Stage {curIdx + 1} of {JOURNEY_STAGES.length}</span>
          </div>
          <Progress value={pct} className="h-2 mb-6" />
          <div>
            {JOURNEY_STAGES.map((s, i) => {
              const done = i < curIdx; const current = i === curIdx; const last = i === JOURNEY_STAGES.length - 1;
              return (
                <div key={s.key} className="flex gap-4" data-testid={`dash-stage-${s.key}`}>
                  <div className="flex flex-col items-center">
                    <div className={`h-7 w-7 rounded-full flex items-center justify-center shrink-0 border-2 ${
                      done ? "bg-status-complete border-status-complete text-white"
                        : current ? "bg-primary border-primary text-primary-foreground ring-4 ring-primary/20"
                          : "bg-background border-border text-muted-foreground"}`}>
                      {done ? <Check className="h-3.5 w-3.5" /> : last ? <KeyRound className="h-3.5 w-3.5" /> : <span className="text-xs font-semibold">{i + 1}</span>}
                    </div>
                    {!last && <div className={`w-0.5 flex-1 min-h-[18px] ${i < curIdx ? "bg-status-complete" : "bg-border"}`} />}
                  </div>
                  <div className="pb-3">
                    <p className={`text-sm font-medium ${current ? "text-primary" : done ? "" : "text-muted-foreground"}`}>{s.label}</p>
                    {current && <p className="text-xs text-muted-foreground mt-0.5 max-w-md">{s.nudge}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="space-y-6">
          {isAtOrPast(curKey, "keys") && <ReviewsReferrals reviewUrl={ms?.review_url} />}
          <Card className="p-6 bg-forest text-white border-0 rounded-2xl">
            <div className="flex items-center gap-2 text-white/80 text-xs font-semibold uppercase tracking-wider">
              <Sparkles className="h-4 w-4" /> Your next step
            </div>
            <p className="font-heading text-lg font-semibold mt-3 leading-snug">{curStage.nudge}</p>
            <Button onClick={() => navigate("/app/journey")} data-testid="next-step-btn"
              className="mt-4 bg-white text-forest hover:bg-white/90 rounded-full">
              View my journey <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Card>

          {agent && (
            <Card className="p-6 rounded-2xl">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Your agent</p>
              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12">
                  <AvatarImage src={agent.picture} />
                  <AvatarFallback className="bg-primary text-primary-foreground">{agent.name?.slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold">{agent.name}</p>
                  <p className="text-sm text-muted-foreground">{agent.email}</p>
                </div>
              </div>
              <Button variant="outline" onClick={() => navigate("/app/assistant")} className="w-full mt-4 rounded-full" data-testid="ask-agent-btn">
                Ask a question
              </Button>
            </Card>
          )}

          {household && household.length > 1 && (
            <Card className="p-6 rounded-2xl" data-testid="household-card">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Your household</p>
              <div className="space-y-2">
                {household.map((m, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm" data-testid={`household-member-${i}`}>
                    <div className="h-7 w-7 rounded-full bg-secondary flex items-center justify-center text-xs font-medium shrink-0">{m.name?.slice(0, 2).toUpperCase()}</div>
                    <span className="flex-1 truncate">{m.name}</span>
                    <span className="text-xs text-muted-foreground">{m.role}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-3">You share homes, notes and comparisons.</p>
            </Card>
          )}
          <InvitePartner />
        </motion.div>
      </div>

      {isAtOrPast(curKey, "under_contract") && ms?.contract?.length > 0 && (
        <div className="mt-6"><ContractTimeline items={ms.contract} /></div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {[
          { label: "Current stage", value: curStage.label, to: "/app/journey" },
          { label: "Homes compared", value: roadmap.homes_count, to: "/app/compare" },
          { label: "Profile", value: roadmap.profile_complete ? "Ready" : "Pending", to: "/app/onboarding" },
          { label: "Offer ready", value: roadmap.offer_ready ? "Yes" : "Not yet", to: "/app/offer" },
        ].map((s) => (
          <Card key={s.label} onClick={() => navigate(s.to)}
            className="p-5 rounded-2xl cursor-pointer hg-card-hover" data-testid={`stat-${s.label.toLowerCase().replace(/ /g, "-")}`}>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{s.label}</p>
            <p className="font-heading text-xl font-bold mt-1 truncate">{s.value}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}
